/**
 * Letter-by-letter reveal for `.letter-reveal-context` (slideshow slides + dynamic grid).
 * Reveal trigger: element enters the viewport (IntersectionObserver). Slideshow also requires the slide to be selected.
 */

function parseCssMs(value) {
  if (!value || typeof value !== 'string') return 0;
  const m = value.trim().match(/^([\d.]+)\s*(ms|s)?$/i);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  return (m[2] || 'ms').toLowerCase() === 's' ? n * 1000 : n;
}

function wrapHeadingLetters(headingRoot) {
  if (!headingRoot || headingRoot.dataset.letterRevealReady) return;

  const walker = document.createTreeWalker(headingRoot, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || node.nodeValue.length === 0) return NodeFilter.FILTER_REJECT;
      if (node.parentElement && node.parentElement.closest('.heading-letter-reveal__clip')) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) textNodes.push(n);

  if (textNodes.length === 0) {
    headingRoot.dataset.letterRevealReady = '1';
    return;
  }

  const rootStyles = getComputedStyle(headingRoot);
  const staggerMs = parseCssMs(rootStyles.getPropertyValue('--lr-stagger')) || 40;
  const initialDelayMs = parseCssMs(rootStyles.getPropertyValue('--lr-delay')) || 0;

  let globalIndex = 0;
  for (const textNode of textNodes) {
    const parent = textNode.parentNode;
    if (!parent) continue;
    const text = textNode.nodeValue;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      if (char === '\n') {
        frag.appendChild(document.createElement('br'));
        continue;
      }
      const outer = document.createElement('span');
      outer.className = 'heading-letter-reveal__clip';
      const inner = document.createElement('span');
      inner.className = 'heading-letter-reveal__inner';
      inner.style.transitionDelay = `${Math.round(initialDelayMs + globalIndex * staggerMs)}ms`;
      inner.textContent = char === ' ' ? '\u00A0' : char;
      outer.appendChild(inner);
      frag.appendChild(outer);
      globalIndex += 1;
    }
    parent.replaceChild(frag, textNode);
  }
  headingRoot.dataset.letterRevealReady = '1';
}

function isInViewport(el) {
  const r = el.getBoundingClientRect();
  return r.bottom > 0 && r.top < window.innerHeight && r.left < window.innerWidth && r.right > 0;
}

function maybeRevealInView(headings) {
  headings.forEach((h) => {
    if (isInViewport(h)) h.setAttribute('data-letter-reveal-visible', '');
  });
}

function markLetterRevealDoneWhenFinished(heading) {
  if (!heading || heading.dataset.letterRevealDone) return;
  const inners = heading.querySelectorAll('.heading-letter-reveal__inner');
  if (!inners.length) {
    heading.dataset.letterRevealDone = '1';
    return;
  }
  const last = inners[inners.length - 1];
  let tid;
  const finish = () => {
    if (heading.dataset.letterRevealDone) return;
    heading.dataset.letterRevealDone = '1';
    if (tid) window.clearTimeout(tid);
  };
  last.addEventListener('transitionend', finish, { once: true });
  tid = window.setTimeout(finish, 6000);
}

/**
 * Slideshow: stacked slides / overflow make IO unreliable — reveal on .is-selected (init + carousel:settle).
 * data-letter-reveal-done: animate once per heading; do not clear visible for done headings on slide change.
 */
function resetAndRevealSlideshowCarousel(carousel) {
  carousel.querySelectorAll('.heading-letter-reveal').forEach((h) => {
    if (!h.dataset.letterRevealDone) h.removeAttribute('data-letter-reveal-visible');
  });
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const selected = carousel.querySelectorAll(
        '.slideshow__slide.is-selected .heading-letter-reveal[data-letter-reveal-ready]'
      );
      selected.forEach((h) => {
        if (h.dataset.letterRevealDone) {
          h.setAttribute('data-letter-reveal-visible', '');
          return;
        }
        void h.offsetWidth;
        h.setAttribute('data-letter-reveal-visible', '');
        markLetterRevealDoneWhenFinished(h);
      });
    });
  });
}

const carouselBound = new WeakSet();

function bindSlideshowCarousels(scope = document) {
  const root = scope.nodeType === Node.ELEMENT_NODE ? scope : document;
  root.querySelectorAll('slideshow-carousel').forEach((carousel) => {
    if (carouselBound.has(carousel)) return;
    carouselBound.add(carousel);
    carousel.addEventListener('carousel:settle', () => resetAndRevealSlideshowCarousel(carousel));
  });
}

let intersectionObserver;

function getLetterRevealObserver() {
  if (intersectionObserver) return intersectionObserver;
  intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const h = entry.target;
        if (!h.classList.contains('heading-letter-reveal') || !h.dataset.letterRevealReady) return;

        if (h.closest('slideshow-carousel')) return;

        if (h.dataset.letterRevealDone) return;

        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          h.setAttribute('data-letter-reveal-visible', '');
          markLetterRevealDoneWhenFinished(h);
          intersectionObserver.unobserve(h);
        }
      });
    },
    { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0 }
  );
  return intersectionObserver;
}

function bindLetterRevealObservers(scope = document) {
  const root = scope.nodeType === Node.ELEMENT_NODE ? scope : document;
  const headings = root.querySelectorAll('.letter-reveal-context .heading-letter-reveal[data-letter-reveal-ready]');

  if (!('IntersectionObserver' in window)) {
    headings.forEach((heading) => {
      const slide = heading.closest('.slideshow__slide');
      if (slide && !slide.classList.contains('is-selected')) return;
      heading.setAttribute('data-letter-reveal-visible', '');
      heading.dataset.letterRevealDone = '1';
    });
    return;
  }

  const io = getLetterRevealObserver();
  headings.forEach((heading) => {
    if (heading.dataset.letterRevealIoBound) return;
    heading.dataset.letterRevealIoBound = '1';
    if (heading.closest('slideshow-carousel')) return;
    io.observe(heading);
  });
}

function initLetterReveal(scope = document) {
  const context = scope && scope.nodeType === Node.ELEMENT_NODE ? scope : document;
  const prefersReduced =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  context.querySelectorAll('.letter-reveal-context .heading-letter-reveal').forEach((el) => {
    if (prefersReduced) {
      el.dataset.letterRevealReady = '1';
      el.setAttribute('data-letter-reveal-visible', '');
      el.dataset.letterRevealDone = '1';
      return;
    }
    wrapHeadingLetters(el);
  });

  bindLetterRevealObservers(context);
  bindSlideshowCarousels(context);

  context.querySelectorAll('slideshow-carousel').forEach((carousel) => {
    resetAndRevealSlideshowCarousel(carousel);
  });
}

function initOnReady(init) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

initOnReady(() => {
  initLetterReveal();
  setTimeout(() => {
    document.querySelectorAll('slideshow-carousel').forEach((carousel) => {
      resetAndRevealSlideshowCarousel(carousel);
    });
  }, 0);
});

document.addEventListener('shopify:section:load', (event) => {
  initLetterReveal(event.target);
  setTimeout(() => {
    event.target?.querySelectorAll?.('slideshow-carousel')?.forEach?.((carousel) => {
      resetAndRevealSlideshowCarousel(carousel);
    });
  }, 0);
});
